// 261012 E1-P4 — hàm THUẦN của panel "Người phụ không tên" (chặng ⑤, cột phải của một tập). KHÔNG import antd / react
// (jest của react-scripts không load được antd v6). `VisualExtrasPanel` / `useVisualExtras` chỉ vẽ + gọi API.
// Hợp đồng dữ liệu: spec `261012-epic1-phase4.md` D2 (cột) + D7 (điều kiện in) + D9 (GET/POST/PATCH + mã lỗi).
import type {
  VisualExtraView, VisualExtrasEditResult, VisualExtrasEpisodeView, VisualExtrasJobResult, VisualExtrasLimits, VisualExtrasRunBody,
  VisualExtrasSummary, VisualRunningJob,
} from '../../../types/adaptation';
import type { WorkbenchHandoffMark } from '../../../types/adaptationWorkbench';
import { friendlyApiMessage, looksTechnical } from './jobErrorText';
import { redactMoney } from './moneyRedact';
import type { DangerCopyView } from './workbenchView';

// ─── Hằng ──────────────────────────────────────────────────────────────────

/** Loại job nền của lượt AI người phụ (`FILM_JOB.VISUAL_EXTRAS`, D8) — dùng để nhận nuôi sau tải lại trang. */
export const VISUAL_EXTRAS_JOB_TYPE = 'FILM_VISUAL_EXTRAS';
/** Dự phòng khi `limits` chưa tải: BE `MAX_LABEL_CHARS` = 30 (đơn vị UTF-16). Trần MÔ TẢ thì KHÔNG hardcode — thiếu `limits` ⇒ không lưu. */
export const DEFAULT_MAX_LABEL = 30;
/** D9: `episodes` tối đa 50 tập mỗi lần POST. */
export const MAX_RUN_EPISODES = 50;

export const EXTRAS_JOB_RUNNING_REASON = 'Đang quét người phụ ở nền';
const STAGE_LOCKED = 'Bước đang khoá';
const NOT_LOADED = 'Chưa tải được danh sách người phụ';
const NO_SCRIPT = 'Viết kịch bản trước';
const WORKING = 'Đang lưu — đợi xong rồi thử lại';
const SHELL_BUSY = 'Đang có tác vụ chạy';

/** Ký tự điều khiển / zero-width / bidi — CHÉP từ BE `INVISIBLE` (`utils/visualIdentity.ts`) để đếm đúng như `normalizeProfileText`. */
// eslint-disable-next-line no-control-regex
const INVISIBLE = new RegExp('[\\u0000-\\u001F\\u007F-\\u009F\\u200B-\\u200F\\u2028\\u2029\\u202A-\\u202E\\u2060-\\u2064\\uFEFF]', 'g');
/** Nhãn bị TỪ CHỐI (không tự sửa): dấu hai chấm (cả toàn góc), xuống dòng, tab, ký tự điều khiển. */
// eslint-disable-next-line no-control-regex
const LABEL_FORBIDDEN = /[:：\u0000-\u001F\u007F-\u009F]/;

// ─── Trạng thái panel ──────────────────────────────────────────────────────

export interface ExtrasCtx {
  /** Chặng ⑤ đang khoá. */
  stageLocked: boolean;
  /** Cờ ghi của hook (đang PATCH / đang POST). */
  working: boolean;
  /** Job `FILM_VISUAL_EXTRAS` đang chạy (phiên này hoặc BE báo). */
  jobRunning: boolean;
  /** Vỏ báo bận vì việc khác (`ctx.busy` của WorkbenchContext). */
  shellBusy: boolean;
  /** Summary đã tải. */
  loaded: boolean;
  loadFailed: boolean;
  /** Lỗi của lần chạy AI gần nhất ("Không thể đề xuất người phụ: …") — danh sách cũ vẫn giữ nguyên. */
  runError?: string | null;
}

export type PanelPhase = 'loading' | 'error' | 'noScript' | 'empty' | 'zero' | 'items';
export type ExtrasTone = 'ai' | 'user' | 'keep' | 'warn' | 'mut';
export interface ExtrasTag { key: string; label: string; tone: ExtrasTone }

export interface PanelView {
  phase: PanelPhase;
  /** Job đang chạy — panel vẫn hiện các mục đang có nhưng khoá mọi nút ghi. */
  running: boolean;
  scriptChanged: boolean;
  title: string;
  count: number;
  /** Dòng giữa panel khi KHÔNG có mục ("Tập này chưa có danh sách…", "Viết kịch bản trước."). */
  message?: string;
  /** Dải cảnh báo khi kịch bản đã đổi sau lần đề xuất. */
  banner?: string;
  errorLine?: string;
  scanLabel: string;
  canScan: boolean;
  canAdd: boolean;
  addReason?: string;
  /** Sửa / Giữ / Xoá từng mục. */
  canEditItems: boolean;
  /** Lý do khoá CHUNG; `undefined` khi rảnh. */
  reasonDisabled?: string;
}

const blockReasonOf = (ctx: ExtrasCtx, hasScript: boolean): string | undefined => {
  if (ctx.stageLocked) return STAGE_LOCKED;
  if (!ctx.loaded) return NOT_LOADED;
  if (!hasScript) return NO_SCRIPT;
  if (ctx.jobRunning) return EXTRAS_JOB_RUNNING_REASON;
  if (ctx.working) return WORKING;
  if (ctx.shellBusy) return SHELL_BUSY;
  return undefined;
};

export const panelState = (
  ep: VisualExtrasEpisodeView | null | undefined, ctx: ExtrasCtx, limits: VisualExtrasLimits | null | undefined,
): PanelView => {
  const base = { running: ctx.jobRunning, scriptChanged: false, count: 0, title: 'Người phụ không tên', scanLabel: 'Đề xuất người phụ (AI)' };
  const off = { canScan: false, canAdd: false, canEditItems: false };
  if (!ctx.loaded) {
    const failed = ctx.loadFailed;
    return {
      ...base, ...off, phase: failed ? 'error' : 'loading', message: failed ? 'Không tải được danh sách người phụ' : '…',
      reasonDisabled: stageOr(ctx, NOT_LOADED),
    };
  }
  // Tập không có trong summary hoặc chưa có kịch bản ⇒ như nhau: không quét / không thêm được.
  if (!ep || !ep.hasScript) {
    return { ...base, ...off, phase: 'noScript', message: 'Viết kịch bản trước.', reasonDisabled: stageOr(ctx, NO_SCRIPT) };
  }
  const items = ep.items ?? [];
  const count = items.length;
  const blocked = blockReasonOf(ctx, true);
  const hasColumn = ep.version !== 'none';
  const phase: PanelPhase = count > 0 ? 'items' : hasColumn ? 'zero' : 'empty';
  const max = limits && limits.maxItems > 0 ? limits.maxItems : null;
  const full = max !== null && count >= max;
  const addReason = blocked ?? (full ? `Đã đủ ${max} người phụ (tối đa ${max}) — xoá bớt rồi thêm` : undefined);
  const scriptChanged = !!ep.scriptChanged;
  return {
    ...base, phase, scriptChanged, count,
    title: count > 0 ? `Người phụ không tên (${count})` : 'Người phụ không tên',
    scanLabel: phase === 'empty' ? 'Đề xuất người phụ (AI)' : 'Quét lại (AI)',
    message: phase === 'empty' ? 'Tập này chưa có danh sách người phụ.' : phase === 'zero' ? 'Đã quét kịch bản: không có người phụ không tên.' : undefined,
    ...(scriptChanged && count > 0
      ? { banner: 'Kịch bản tập này đã đổi sau lần đề xuất — các mục “AI đề xuất” chưa Giữ sẽ KHÔNG vào khối. Bấm “Quét lại” hoặc sửa tay + Giữ để đưa vào khối.' }
      : {}),
    ...(ctx.runError ? { errorLine: ctx.runError } : {}),
    canScan: !blocked,
    canAdd: !addReason,
    ...(addReason ? { addReason } : {}),
    canEditItems: !blocked && count > 0,
    ...(blocked ? { reasonDisabled: blocked } : {}),
  };
};

/** Khoá chặng thắng lý do khác (người dùng cần biết chặng đang khoá trước). */
const stageOr = (ctx: ExtrasCtx, other: string): string => (ctx.stageLocked ? STAGE_LOCKED : other);

// ─── Từng mục ──────────────────────────────────────────────────────────────

export interface ItemView {
  id: string;
  label: string;
  text: string;
  tags: ExtrasTag[];
  /** Câu trích nguyên văn của AI (rỗng ⇒ không hiện). */
  evidence?: string;
  /** Dòng khối Film SẼ in; `null` khi không in (AI đã cũ) hoặc BE không in được. */
  blockLine: string | null;
  /** Dòng chú nhỏ (vì sao không in). */
  note?: string;
  /** AI chưa Giữ + kịch bản đã đổi ⇒ khối KHÔNG in mục này (D7). */
  willNotPrint: boolean;
  /** `printed === false` mà KHÔNG phải stale: vượt trần 5 dòng hoặc trùng nhãn với nhân vật/bối cảnh — thẻ trung tính. */
  notPrintedOther: boolean;
  canEdit: boolean;
  canLock: boolean;
  canRemove: boolean;
  removeReason?: string;
}

const STALE_TAG = 'Kịch bản đã đổi — mục này sẽ KHÔNG in';
const NOT_PRINTED_TAG = 'Không in (vượt trần 5 dòng hoặc trùng nhãn với nhân vật/bối cảnh)';
const STALE_NOTE = 'Kịch bản tập này được viết lại sau lần đề xuất. Bấm “Quét lại” hoặc sửa tay + Giữ để đưa vào khối.';

export const itemView = (it: VisualExtraView, panel: PanelView): ItemView => {
  const user = it.status === 'USER_EDITED';
  const tags: ExtrasTag[] = [user ? { key: 'status', label: 'Đã sửa tay', tone: 'user' } : { key: 'status', label: 'AI đề xuất', tone: 'ai' }];
  const hasEvidence = typeof it.evidence === 'string' && it.evidence.trim() !== '';
  if (it.source === 'SOURCE_MENTION' || (it.source !== 'USER' && it.source !== 'AI_INFERRED' && hasEvidence)) {
    tags.push({ key: 'source', label: 'Nguyên văn', tone: 'mut' });
  } else if (it.source === 'AI_INFERRED') {
    tags.push({ key: 'source', label: 'AI suy luận (không có câu trích)', tone: 'warn' });
  }
  if (it.locked) tags.push({ key: 'keep', label: 'Giữ', tone: 'keep' });
  // `stale` do BE tính (D7): AI_PROPOSED chưa Giữ + kịch bản đã đổi sau lần quét. In được ⇔ `blockLine !== null`.
  const willNotPrint = it.stale === true;
  if (willNotPrint) tags.push({ key: 'stale', label: STALE_TAG, tone: 'warn' });
  const notPrintedOther = !willNotPrint && it.printed === false;
  if (notPrintedOther) tags.push({ key: 'capped', label: NOT_PRINTED_TAG, tone: 'mut' });
  const removeReason = panel.reasonDisabled ?? (it.locked ? 'Đang Giữ — bỏ Giữ mới xoá được' : undefined);
  return {
    id: it.id, label: it.label, text: it.text, tags,
    ...(hasEvidence ? { evidence: it.evidence as string } : {}),
    blockLine: willNotPrint || notPrintedOther ? null : (it.blockLine ?? null),
    ...(willNotPrint ? { note: STALE_NOTE } : {}),
    willNotPrint,
    notPrintedOther,
    canEdit: panel.canEditItems,
    canLock: panel.canEditItems,
    canRemove: panel.canEditItems && !it.locked,
    ...(removeReason ? { removeReason } : {}),
  };
};

// ─── Ô sửa tay ─────────────────────────────────────────────────────────────

export interface ExtraValidation {
  /** Đơn vị UTF-16 (`.length`, đúng như `sanitizeBlockLabel` / `normalizeProfileText` của BE) trên bản đã chuẩn hoá. */
  labelCount: number;
  labelMax: number;
  textCount: number;
  /** `null` khi `limits` chưa tải ⇒ KHÔNG cho lưu (không hardcode trần mô tả). */
  textMax: number | null;
  /** Nhãn đã bỏ ký tự vô hình, gộp khoảng trắng, trim — thứ nên gửi lên. */
  normalizedLabel: string;
  /** Mô tả MỘT dòng — thứ nên gửi lên. */
  normalizedText: string;
  ok: boolean;
  /** Mã lỗi (cùng tập mã với BE) của lỗi ĐẦU TIÊN; nhãn kiểm trước mô tả. */
  code?: 'EMPTY_LABEL' | 'LABEL_TOO_LONG' | 'LABEL_INVALID' | 'EMPTY_TEXT' | 'TOO_LONG';
  message?: string;
}

/** Mô tả → MỘT dòng như `normalizeProfileText`: tab/xuống dòng → cách, bỏ ký tự vô hình, gộp khoảng trắng. */
const oneLine = (raw: string): string => String(raw ?? '').replace(/[\t\n\r\v\f]+/g, ' ').replace(INVISIBLE, '').replace(/\s+/g, ' ').trim();

export const editValidation = (label: string, text: string, limits: VisualExtrasLimits | null | undefined): ExtraValidation => {
  const labelMax = limits && limits.maxLabel > 0 ? limits.maxLabel : DEFAULT_MAX_LABEL;
  const textMax = limits && limits.maxText > 0 ? limits.maxText : null;
  const rawLabel = String(label ?? '').trim();
  const normalizedLabel = rawLabel.replace(INVISIBLE, '').replace(/\s+/g, ' ').trim();
  const normalizedText = oneLine(text);
  const labelCount = normalizedLabel.length;
  const textCount = normalizedText.length;
  let code: ExtraValidation['code'];
  if (labelCount === 0) code = 'EMPTY_LABEL';
  else if (LABEL_FORBIDDEN.test(rawLabel)) code = 'LABEL_INVALID';
  else if (labelCount > labelMax) code = 'LABEL_TOO_LONG';
  else if (textCount === 0) code = 'EMPTY_TEXT';
  else if (textMax !== null && textCount > textMax) code = 'TOO_LONG';
  const message = code
    ? errorText(code, { limits: { maxItems: 0, maxLabel: labelMax, maxText: textMax ?? 0 } }).text
    : textMax === null ? 'Chưa tải được giới hạn — thử lại sau' : undefined;
  return {
    labelCount, labelMax, textCount, textMax, normalizedLabel, normalizedText,
    ok: !code && textMax !== null, ...(code ? { code } : {}), ...(message ? { message } : {}),
  };
};

/** Xem thử dòng khối Film sẽ in (gần đúng — dòng thật do server tính, `blockLine`). Người phụ KHÔNG có tiền tố (khác `[Setting] `). Nhãn đang bị từ chối ⇒ rỗng. */
export const previewBlockLine = (label: string, text: string): string => {
  const raw = String(label ?? '').trim();
  const lab = raw.replace(INVISIBLE, '').replace(/\s+/g, ' ').trim();
  const desc = oneLine(text);
  if (!lab || !desc || LABEL_FORBIDDEN.test(raw)) return '';
  return `${lab}: ${desc}`;
};

// ─── Chạy AI (tốn AI) ────────────────────────────────────────────────────

export type RunScope = 'missing' | 'one';

/** Thời gian đo thật mỗi tập (10–16 giây, chạy lần lượt). */
export const MEASURED_DURATION_TEXT = '≈ 10–16 giây mỗi tập, chạy lần lượt';

export interface RunConfirmInput {
  scope: RunScope;
  /** Số tập sẽ chạy lần này. */
  count: number;
  /** `scope:'one'`: tập đã có người phụ AI ⇒ "Quét lại". */
  redo?: boolean;
  episode?: number;
  /** Có nhiều hơn 50 tập chưa có ⇒ lần này chỉ chạy `count` tập đầu trong `total`. */
  capped?: { total: number };
}

/** Các hộp hỏi lại theo thứ tự — chỉ còn một hộp xác nhận (261015: bỏ hộp "truyện thật"). Mọi POST đi qua hàm này. */
export const runConfirmCopy = (i: RunConfirmInput): { steps: DangerCopyView[] } => {
  const keep = 'Mục đã sửa tay hoặc đã Giữ không bị đổi.';
  let first: DangerCopyView;
  if (i.scope === 'one') {
    const ep = i.episode ?? 0;
    first = i.redo
      ? {
        title: `Quét lại người phụ của tập ${ep}?`, okText: 'Quét lại', danger: false,
        content: `Chạy AI đọc lại kịch bản tập ${ep} và thay các mục “AI đề xuất” bằng kết quả mới (có thể khác lần trước), ${MEASURED_DURATION_TEXT}. ${keep}`,
      }
      : {
        title: `Đề xuất người phụ cho tập ${ep}?`, okText: 'Chạy AI', danger: false,
        content: `Chạy AI đọc kịch bản tập ${ep} và đề xuất tối đa vài người phụ không tên, ${MEASURED_DURATION_TEXT}. ${keep}`,
      };
  } else {
    const which = i.capped ? `${i.count} tập đầu trong ${i.capped.total} tập chưa có (phần còn lại chạy ở lần sau)` : `${i.count} tập`;
    first = {
      title: `Đề xuất người phụ cho ${i.count} tập?`, okText: 'Chạy AI', danger: false,
      content: `Chạy AI cho ${which}. Mỗi tập là một lượt AI, chạy nền, ${MEASURED_DURATION_TEXT}. ${keep}`,
    };
  }
  const steps: DangerCopyView[] = [first];
  return { steps };
};

export interface RunBodyInput { storyId: string; episodes: number[]; redo: boolean }

/** Body `POST visual-extras`. Luôn gửi `episodes` RÕ RÀNG, không bao giờ rỗng. */
export const buildRunBody = (i: RunBodyInput): VisualExtrasRunBody => {
  if (!i.episodes.length) throw new Error('buildRunBody: cần ít nhất một tập');
  const body: VisualExtrasRunBody = { storyId: i.storyId, episodes: i.episodes, redo: i.redo };
  return body;
};

export interface RunPlanInput {
  storyId: string;
  scope: RunScope;
  episodes: number[];
  /** `scope:'one'`: tập đang có mục `AI_PROPOSED` ⇒ chữ hộp là "Quét lại … thay …"; KHÔNG ảnh hưởng `body.redo`. */
  hasAi?: boolean;
}
export interface RunPlan { steps: DangerCopyView[]; body: VisualExtrasRunBody; capped: boolean }

/** Hộp hỏi + body đi CÙNG NHAU. Quá 50 tập ⇒ 50 tập đầu. */
export const planRun = (i: RunPlanInput): RunPlan => {
  const batch = i.episodes.slice(0, MAX_RUN_EPISODES);
  const capped = i.episodes.length > MAX_RUN_EPISODES;
  // 🔴 B1: một tập (bấm "Quét lại"/"Đề xuất" tường minh) LUÔN `redo:true` — BE `needsExtrasScan(redo=false)` bỏ qua tập đã có `proposedAt`, người dùng trả tiền
  // mà job "bỏ qua". `redo` ở BE chỉ thay mục AI_PROPOSED, giữ USER_EDITED/Giữ ⇒ an toàn. Hàng loạt (`missing`) giữ `redo:false`.
  const redo = i.scope === 'one';
  const { steps } = runConfirmCopy({
    scope: i.scope, count: batch.length, redo: i.scope === 'one' && i.hasAi === true, episode: i.scope === 'one' ? batch[0] : undefined,
    ...(capped ? { capped: { total: i.episodes.length } } : {}),
  });
  return { steps, body: buildRunBody({ storyId: i.storyId, episodes: batch, redo }), capped };
};

/**
 * Tập "chưa có" người phụ = CÓ kịch bản + chưa từng có cột (`version` "none") + không mục nào. Tập đã quét mà AI không thấy ai (có cột, 0 mục)
 * KHÔNG tính — không tốn tiền quét lại; muốn quét lại thì bấm "Quét lại" ở tập đó. Sắp tăng dần.
 */
export const missingEpisodes = (s: VisualExtrasSummary | null | undefined): number[] =>
  (s?.episodes ?? [])
    .filter((e) => e.hasScript && e.version === 'none' && (e.items?.length ?? 0) === 0)
    .map((e) => e.episode)
    .sort((a, b) => a - b);

// ─── Thanh hàng loạt (chặng ⑤) ─────────────────────────────────────────────

export interface BulkBarView {
  chips: Array<{ key: string; label: string; tone: ExtrasTone }>;
  runLabel: string;
  canRun: boolean;
  runReason?: string;
  /** Dòng chú cạnh nút: đang chạy, hoặc "chạy nền · không đụng mục đã sửa tay/Giữ". */
  hint: string;
}

/** `missing` = `missingEpisodes(summary)`. */
export const bulkBarView = (
  s: VisualExtrasSummary | null | undefined, ctx: ExtrasCtx, missing: ReadonlyArray<number>,
): BulkBarView => {
  const n = missing.length;
  const withScript = (s?.episodes ?? []).filter((e) => e.hasScript).length;
  const chips: BulkBarView['chips'] = ctx.loaded && s
    ? [
      { key: 'scripts', label: `${withScript} tập có kịch bản`, tone: 'mut' },
      ...(n > 0 ? [{ key: 'missing', label: `${n} tập chưa có người phụ`, tone: 'warn' as const }] : []),
    ]
    : [];
  const runReason = ctx.stageLocked ? STAGE_LOCKED
    : !ctx.loaded ? NOT_LOADED
      : ctx.jobRunning ? EXTRAS_JOB_RUNNING_REASON
        : ctx.working ? WORKING
          : ctx.shellBusy ? SHELL_BUSY
            : n === 0 ? 'Mọi tập có kịch bản đều đã có danh sách người phụ' : undefined;
  const hint = ctx.jobRunning
    ? EXTRAS_JOB_RUNNING_REASON
    : 'chạy nền, lần lượt từng tập · không đụng mục đã sửa tay/đã Giữ';
  return { chips, runLabel: `Đề xuất người phụ cho các tập chưa có (${n})`, canRun: !runReason, ...(runReason ? { runReason } : {}), hint };
};

// ─── Lỗi ───────────────────────────────────────────────────────────────────

export interface ErrorTextCtx { term?: string; limits?: VisualExtrasLimits | null; message?: string }

export const errorText = (code: string | undefined, c: ErrorTextCtx = {}): { text: string } => {
  const server = c.message && !looksTechnical(c.message, { loose: true }) ? redactMoney(c.message.trim()) : '';
  const lim = c.limits;
  switch (code) {
    case 'EMPTY_LABEL': return { text: 'Chưa nhập nhãn.' };
    case 'LABEL_TOO_LONG': return { text: `Nhãn quá dài${lim?.maxLabel ? ` (tối đa ${lim.maxLabel} ký tự)` : ''}.` };
    case 'LABEL_INVALID': return { text: 'Nhãn không được chứa dấu hai chấm hay xuống dòng — bỏ đi rồi lưu lại.' };
    case 'EMPTY_TEXT': return { text: 'Chưa nhập mô tả.' };
    case 'TOO_LONG': return { text: `Mô tả quá dài${lim?.maxText ? ` (tối đa ${lim.maxText} ký tự)` : ''}.` };
    case 'NAME_LEAK': return { text: `Còn chứa tên gốc${c.term ? ` “${c.term}”` : ''} — dùng tên trong phim hoặc bỏ tên.` };
    case 'DUPLICATE_LABEL': return { text: 'Trùng nhãn với người phụ khác hoặc với nhân vật có tên — đặt nhãn khác.' };
    case 'LOCKED': return { text: 'Đang Giữ — bỏ Giữ rồi mới xoá được.' };
    case 'NO_ITEM': return { text: 'Mục này không còn nữa — đã tải lại danh sách.' };
    case 'VERSION_CHANGED': return { text: 'Danh sách vừa được cập nhật ở nơi khác — đã tải bản mới, phần bạn đang gõ vẫn còn. Bấm Lưu lần nữa nếu vẫn muốn ghi.' };
    case 'TOO_MANY': return { text: `Đã đủ số người phụ cho tập này${lim?.maxItems ? ` (tối đa ${lim.maxItems})` : ''} — xoá bớt rồi thêm.` };
    case 'JOB_RUNNING': return { text: server ? `${server} — đợi xong rồi thử lại.` : 'Đang có tác vụ chạy ở nền — đợi xong rồi thử lại.' };
    case 'SCRIPT_MISSING': return { text: 'Tập này chưa có kịch bản — viết kịch bản trước.' };
    default: return { text: friendlyApiMessage(c.message, 'Không thực hiện được') };
  }
};

export type FreshExtras = VisualExtrasEditResult;

/** Bản hiện tại mà BE đính kèm vào 409 `VERSION_CHANGED` — thân ở gốc, ở `data` hoặc ở `current` (BE chưa chốt chỗ). Hình lạ ⇒ `null`. */
export const freshOf = (d: unknown): FreshExtras | null => {
  const root = (d ?? null) as Record<string, unknown> | null;
  if (!root || typeof root !== 'object') return null;
  for (const c of [root, root.data, root.current]) {
    const o = c as Record<string, unknown> | null | undefined;
    if (o && typeof o === 'object' && typeof o.episode === 'number' && typeof o.version === 'string' && Array.isArray(o.items)) {
      return { episode: o.episode, version: o.version, items: o.items as VisualExtraView[] };
    }
  }
  return null;
};

/** Mã lỗi mà trạng thái đang hiện đã cũ ⇒ tải lại + đóng ô sửa. */
const STALE_CODES: ReadonlySet<string> = new Set(['LOCKED', 'NO_ITEM', 'SCRIPT_MISSING', 'JOB_RUNNING']);

export interface ExtrasError {
  code: string | undefined;
  text: string;
  /** `true` ⇒ giữ ô sửa mở (lỗi nhập, hoặc 409 để người dùng không mất bản nháp). */
  keepForm: boolean;
  /** `true` ⇒ phải tải lại summary (trạng thái đã cũ và không có bản mới đính kèm). */
  reload: boolean;
  /** 409 `VERSION_CHANGED` có kèm bản hiện tại. */
  fresh: FreshExtras | null;
}

/** Đọc lỗi axios của PATCH/POST người phụ. */
export const extrasErrorOf = (err: unknown, limits?: VisualExtrasLimits | null): ExtrasError => {
  const d = (err as { response?: { data?: Record<string, unknown> } } | null | undefined)?.response?.data;
  const code = typeof d?.code === 'string' ? d.code : undefined;
  const { text } = errorText(code, {
    term: typeof d?.term === 'string' ? d.term : undefined,
    limits,
    message: typeof d?.message === 'string' ? d.message : undefined,
  });
  if (code === 'VERSION_CHANGED') {
    const fresh = freshOf(d);
    return { code, text, keepForm: true, reload: !fresh, fresh };
  }
  // 409 "bận" của BE (`{busy:true, jobType, startedAt, runningMinutes, message}`) KHÔNG có `code`: coi như JOB_RUNNING nhưng giữ bản nháp.
  if (!code && d?.busy === true) {
    return { code: 'JOB_RUNNING', text: errorText('JOB_RUNNING', { message: typeof d.message === 'string' ? d.message : undefined }).text, keepForm: true, reload: true, fresh: null };
  }
  const stale = !!code && STALE_CODES.has(code);
  return { code, text, keepForm: !stale, reload: stale, fresh: null };
};

/** Dán thân 200 (hoặc bản mới của 409) vào summary: chỉ đổi đúng tập. Không đột biến; tập lạ ⇒ trả lại chính `s`. */
export const applyEditResult = (s: VisualExtrasSummary | null, r: VisualExtrasEditResult): VisualExtrasSummary | null => {
  if (!s) return s;
  if (!s.episodes.some((e) => e.episode === r.episode)) return s;
  return { ...s, episodes: s.episodes.map((e) => (e.episode === r.episode ? { ...e, version: r.version, items: r.items } : e)) };
};

// ─── Kết quả job ───────────────────────────────────────────────────────────

/**
 * Toast khi job xong (`task.detail.result` phẳng, D8). Trung thực: BE có thể báo `skipped` (chữ lý do), `remainingEpisodes` (tập CHƯA chạy vì hết hạn mềm 40 phút)
 * hoặc 0 tập/0 mục/0 lỗi ⇒ KHÔNG nói "Đã quét 0 tập…" và không tuyên bố xong cả lô khi còn tập chưa chạy.
 */
export const jobDoneText = (r: VisualExtrasJobResult | null | undefined): string => {
  const hasDone = typeof r?.episodesDone === 'number';
  const hasApplied = typeof r?.appliedCount === 'number';
  const done = hasDone ? (r?.episodesDone as number) : 0;
  const applied = hasApplied ? (r?.appliedCount as number) : 0;
  const skipped = typeof r?.skipped === 'string' ? r.skipped.trim() : '';
  const failed = typeof r?.episodesFailed === 'number' && r.episodesFailed > 0 ? r.episodesFailed : 0;
  const remaining = Array.isArray(r?.remainingEpisodes) ? r?.remainingEpisodes.length : 0;
  if (!r || (!hasDone && !hasApplied && !skipped && !failed && !remaining)) return 'Đã xong đề xuất người phụ';
  const tail = [
    ...(failed ? [`${failed} tập lỗi/bị bỏ`] : []),
    ...(remaining ? [`còn ${remaining} tập chưa chạy (hết 40 phút) — bấm chạy lại để làm tiếp`] : []),
  ];
  if (done === 0 && applied === 0) {
    if (skipped) return ['Không quét tập nào', skipped, ...tail].join(' · ');
    if (remaining) return ['Chưa quét tập nào', ...tail].join(' · ');
    if (failed) return ['Không quét được tập nào', ...tail].join(' · ');
    if (hasDone) return 'Không có tập nào cần đề xuất người phụ — chưa gọi AI';
  }
  const parts: string[] = [];
  if (hasDone) parts.push(`Đã quét ${done} tập`);
  if (hasApplied) parts.push(applied > 0 ? `thêm ${applied} người phụ` : 'không thêm người phụ nào');
  if (skipped) parts.push(skipped);
  return [...parts, ...tail].join(' · ');
};

/** Kết quả không trọn vẹn (còn tập chưa chạy / có tập lỗi / có `skipped`) ⇒ toast cảnh báo thay vì thành công. */
export const jobDoneIsPartial = (r: VisualExtrasJobResult | null | undefined): boolean =>
  !!r && ((r.remainingEpisodes?.length ?? 0) > 0 || (r.episodesFailed ?? 0) > 0 || (typeof r.skipped === 'string' && r.skipped.trim() !== ''));

/** Job `FILM_VISUAL_EXTRAS` đang chạy trong `summary.runningJobs` ⇒ id để nhận nuôi sau tải lại trang. */
export const pickExtrasJobId = (jobs: ReadonlyArray<Pick<VisualRunningJob, 'id' | 'type'>> | null | undefined): string | null =>
  (jobs ?? []).find((j) => j?.type === VISUAL_EXTRAS_JOB_TYPE)?.id ?? null;

// ─── Sau khi lưu (D10) ─────────────────────────────────────────────────────

/**
 * Nhắn sau khi ghi: chương nào đã đẩy sang Chapters thì phải đẩy lại; chương đã có Film plan / đã sửa tay không tự đổi.
 * Chỉ dùng dữ liệu đã có (`workbench.episodes[].handoff`) — không gọi API mới; thiếu dữ liệu ⇒ chữ chung.
 */
export const afterSaveHint = (mark: WorkbenchHandoffMark | undefined | null): string => {
  const tail = 'Chương đã có Film plan hoặc đã sửa tay sẽ không tự đổi.';
  if (mark === 'none') return 'Tập này chưa đẩy sang Chapters — lần đẩy tới sẽ lấy danh sách này.';
  if (mark) return `Tập này đã đẩy sang Chapters — cần đẩy lại (nút “Đẩy sang Chapters”) để khối nhân vật cập nhật. ${tail}`;
  return `Chương đã đẩy cần đẩy lại (nút “Đẩy sang Chapters”) để khối nhân vật cập nhật. ${tail}`;
};
